
import {timer as observableTimer, throwError as observableThrowError, of as observableOf,  Observable ,  Subscription } from 'rxjs';

import {switchMap, mergeMap, map, catchError} from 'rxjs/operators';
import { EcaWindowRefService } from './../window-ref/window-ref.service';
import { JwtHelperService } from '@auth0/angular-jwt';
import { EcaApiGatewayService } from './../api-gateway/api-gateway.service';
import { Injectable, NgZone, inject, provideAppInitializer } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { EcaEnvironmentService } from '../environment/environment.service';
import { AppContextService } from '@drc-eca/eca-components-lib';

@Injectable({
  providedIn: 'root'
})
export class EcaSecurityAuthenticationService {
  private http = inject(HttpClient);
  private apiGatewayService = inject(EcaApiGatewayService);
  private jwtHelperService = inject(JwtHelperService);
  private environmentService = inject(EcaEnvironmentService);
  private windowRefService = inject(EcaWindowRefService);
  private appContextService = inject(AppContextService);
  private ngZone = inject(NgZone);

  private token: string;
  private user;
  private user$;
  private _securityServiceUrl: string;
  private refreshSubscription: Subscription;
  allow: any;
  throwUser: boolean;
  authenticated: boolean;

  private securityServiceUrl() {
    if (this._securityServiceUrl) {
      return this._securityServiceUrl;
    }

    return (this._securityServiceUrl = this.apiGatewayService.lookup('eca-security-service'));
  }

  login(username: string, password: string): Observable<any> {
    return this.http.post(`${this.securityServiceUrl()}/authenticate`, { username, password }).pipe(mergeMap(resJson => {
      const token = resJson['token'];
      this.setToken(token);
      return this.getUser();
    }));
  }

  logout(): Observable<boolean> {
    this.windowRefService.nativeWindow.sessionStorage.removeItem('ngStorage-ecatoken');
    this.token = null;

    if (this.refreshSubscription) {
      this.refreshSubscription.unsubscribe();
    }

    return observableOf(true);
  }

  refresh(): Observable<string> {
    const token = this.token;

    if (!token) {
      return observableThrowError('No token');
    }

    return this.http.post(`${this.securityServiceUrl()}/refresh`, { token }).pipe(map(resJson => {
      const newToken = resJson['token'];
      this.setToken(newToken);
      return newToken;
    }));
  }

  isAuthenticated(): Observable<boolean> {
    const token = this.token;

    if (!token) {
      return observableOf(false);
    }

    return this.http
      .get(`${this.securityServiceUrl()}/authenticate/${token}`).pipe(
      map(() => true),
      catchError(() => observableOf(false)), );
  }

  getUser(): Observable<any> {
    const token = this.token;
    if (!token) {
      return observableThrowError('No token');
    }

    if (this.user) {
      return observableOf(this.user);
    }

    if (this.user$) {
      return this.user$;
    }

    const decodedToken = this.jwtHelperService.decodeToken(token);
    const uid = decodedToken['sub'];

    return this.user$ =
      this.http.get(`${this.securityServiceUrl()}/user/${uid}`).pipe(
        map(resJson => {
          this.user$ = null;
          return this.user = resJson;
        }));
  }

  load(): Promise<any> {
    return this.environmentService.load().then(() => {
      let isTokenExpired;

      // Token is passed via query string from portal-v1 and portal-v2 when app is on a different origin (i.e. local development)
      let token = this.getParameterByName('t');
      try {
        isTokenExpired = this.jwtHelperService.isTokenExpired(token);
      } catch {
        // Catch
      }

      if (isTokenExpired === false) {
        return this.setToken(token);
      }

      // Token is passed via sessionStorage from portal-v2 when both apps are hosted on the same origin
      token = this.windowRefService.nativeWindow.sessionStorage.getItem('ngStorage-ecatoken');
      try {
        token = JSON.parse(token);
      } catch {
        // Catch
      }

      try {
        isTokenExpired = this.jwtHelperService.isTokenExpired(token);
      } catch {
        // Catch
      }

      if (isTokenExpired === false) {
        return this.setToken(token);
      }

      this.windowRefService.nativeWindow.console.log('No token present At initialization');
    });
  }

  private initializeTokenRefresh() {
    const token = this.token;
    const decodedToken = this.jwtHelperService.decodeToken(token);
    const expireTime = decodedToken['exp'] * 1000;
    const nowTime = Date.now();
    const refreshMargin = 2 * 60 * 1000; // two minutes
    const delay = expireTime - nowTime - refreshMargin;

    if (this.refreshSubscription) {
      this.refreshSubscription.unsubscribe();
    }

    this.ngZone.runOutsideAngular(() => {
      this.refreshSubscription = observableTimer(delay).pipe(
        switchMap(() => {
          return this.ngZone.run(() => this.refresh());
        }))
        .subscribe();
    });
  }

  private setToken(token: string) {
    const sessionStorage = this.windowRefService.nativeWindow.sessionStorage;
    this.token = token;
    sessionStorage.setItem('ngStorage-ecatoken', JSON.stringify(token));
    if (this.appContextService.getHeaderVersion() !== 'v2') {
      this.initializeTokenRefresh();
    }
    return token;
  }

  getToken() {
    return this.token;
  }

  private getParameterByName(name, url?) {
    if (name == null) {
      return null;
    }

    if (url == null) {
      url = this.windowRefService.nativeWindow.location.href;
    }

    name = name.replace(/[[]]/g, '\\$&');
    const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)', 'i');
    const results = regex.exec(url);
    if (!results) {
      return null;
    }
    if (!results[2]) {
      return '';
    }
    return decodeURIComponent(results[2].replace(/\+/g, ' '));
  }
}

export function loadTokenFactory(ecaSecurityAuthenticationService: EcaSecurityAuthenticationService) {
  return () => ecaSecurityAuthenticationService.load();
}

export const ECA_SECURITY_AUTHENTICATION_APP_INITIALIZER_PROVIDER = provideAppInitializer(() => {
        const initializerFn = (loadTokenFactory)(inject(EcaSecurityAuthenticationService));
        return initializerFn();
      });
